import React from 'react';
import { Tooltip } from '../../Tooltip';

interface ReadingProgressIndicatorProps {
  viewedPages: number;
  totalPages: number;
  className?: string;
  hideLabel?: boolean;
}

export const ReadingProgressIndicator: React.FC<ReadingProgressIndicatorProps> = ({
  viewedPages,
  totalPages,
  className = '',
  hideLabel = false,
}) => {
  const radius = 12;
  const circumference = 2 * Math.PI * radius;
  const clampedViewed = Math.min(viewedPages, totalPages);
  const percentage = totalPages > 0 ? Math.round((clampedViewed / totalPages) * 100) : 0;
  const offset = circumference - (percentage / 100) * circumference;
  const isComplete = percentage === 100;

  const progressColor = isComplete
    ? 'stroke-green-500'
    : percentage > 0
      ? 'stroke-cyan-500'
      : 'stroke-red-500';

  return (
    <Tooltip
      text="ClinLexis is an aid to your review. Always apply your own professional judgement."
      variant="light-blue"
      position="bottom"
      align="right"
    >
    <div className={`flex items-center gap-1.5 ${className}`}>
      <svg width={32} height={32} viewBox="0 0 32 32" className="shrink-0">
        {/* Background circle */}
        <circle
          cx={16}
          cy={16}
          r={radius}
          fill="none"
          strokeWidth={3}
          className="stroke-medical-200 dark:stroke-medical-700"
        />
        {/* Progress arc */}
        <circle
          cx={16}
          cy={16}
          r={radius}
          fill="none"
          strokeWidth={3}
          strokeLinecap="round"
          className={progressColor}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 16 16)"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>

      {!hideLabel && (
        <span className="text-[10px] text-medical-500 dark:text-medical-400 whitespace-nowrap">
          {isComplete ? (
            <>
              <span className="text-green-600 dark:text-green-400 mr-0.5">&#10003;</span>
              {clampedViewed} of {totalPages} pages reviewed (100%)
            </>
          ) : (
            <>
              <span className="text-amber-500 mr-0.5">&#9888;</span>
              {clampedViewed} of {totalPages} pages reviewed ({percentage}%)
            </>
          )}
        </span>
      )}
    </div>
    </Tooltip>
  );
};
